import React, { useState, useEffect, useRef, useCallback } from 'react';
import MetaTags from "react-meta-tags";
import { Link, useHistory } from "react-router-dom";
import { Button, Container, Row, Col, CardHeader, Form, FormGroup, Label, Input } from 'reactstrap';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import ApiClient from '../BaseUrl/ApiClient';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import Cropper from 'react-easy-crop';
import { getDLDealCroppedImg } from '../../Components/Common/DealCropDL';

function AddBlogs() {
  const history = useHistory();
  const fileInputRef = React.useRef(null);
  const quillRef = useRef(null);
  
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [content, setContent] = useState('');
  const [featuredImage, setFeaturedImage] = useState(null);
  const [imagePreview, setImagePreview] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // Cropper states
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [showCropper, setShowCropper] = useState(false);
  const [imageToCrop, setImageToCrop] = useState(null);
  const [selectedFileName, setSelectedFileName] = useState('No file chosen');


  const CROP_WIDTH = 1104;
  const CROP_HEIGHT = 383.997;
  const CROP_ASPECT = CROP_WIDTH / CROP_HEIGHT;

  // Custom toolbar without image/video options
  const modules = React.useMemo(() => ({
    toolbar: {
      container: [
        [{ 'header': [1, 2, 3, false] }],
        ['bold', 'italic', 'underline', 'strike'],
        [{ 'list': 'ordered'}, { 'list': 'bullet' }],
        [{ 'indent': '-1'}, { 'indent': '+1' }],
        ['blockquote'],
        ['link'],
        ['clean']
      ],
      handlers: {
        image: () => {
          toast.info("Image upload is not allowed in the content area. Please use the Featured Image field.");
        },
        video: () => {
          toast.info("Video upload is not allowed.");
        }
      }
    }
  }), []);

  const formats = [
    'header',
    'bold', 'italic', 'underline', 'strike',
    'list', 'bullet', 'indent',
    'blockquote',
    'link'
  ];

  useEffect(() => {
    return () => {
      if (imagePreview) {
        URL.revokeObjectURL(imagePreview);
      }
    };
  }, [imagePreview]);

  useEffect(() => {
    const removeImageVideoButtons = () => {
      const toolbars = document.querySelectorAll('.ql-toolbar');
      toolbars.forEach(toolbar => {
        const imageButton = toolbar.querySelector('.ql-image');
        const videoButton = toolbar.querySelector('.ql-video');
        
        if (imageButton) imageButton.style.display = 'none';
        if (videoButton) videoButton.style.display = 'none';
      });
    };

    removeImageVideoButtons();
    const timer = setTimeout(removeImageVideoButtons, 500);
    
    return () => clearTimeout(timer);
  }, []);

  const onCropComplete = useCallback((croppedArea, croppedAreaPixels) => {
    setCroppedAreaPixels(croppedAreaPixels);
  }, []);

  const handleImageUpload = useCallback((e) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      setSelectedFileName(file.name);
      
      if (!file.type.match('image.*')) {
        toast.error("Please select a valid image file (JPEG, PNG, GIF, etc.)");
        e.target.value = '';
        return;
      }

      const reader = new FileReader();

      reader.onload = (event) => {
        setImageToCrop(event.target.result);
        setShowCropper(true);
        setZoom(1); // Reset zoom when opening cropper
        setCrop({ x: 0, y: 0 }); // Reset crop position
        e.target.value = '';
      };

      reader.onerror = () => {
        toast.error('Error reading file');
      };

      reader.readAsDataURL(file);
    }
  }, []);

  const handleCropComplete = async () => {
    if (!croppedAreaPixels) {
      toast.error('Please adjust the crop area before saving');
      return;
    }

    try {
      setShowCropper(false);
      toast.loading('Cropping image...', { toastId: 'cropping' });

      // Use your utility function with EXACT dimensions
      const croppedImageBlob = await getDLDealCroppedImg(
        imageToCrop,
        croppedAreaPixels,
        CROP_WIDTH,     // Exactly 1104
        CROP_HEIGHT     // Exactly 383.997
      );

      if (!croppedImageBlob) {
        throw new Error('Failed to create cropped image');
      }

      // Verify the blob has the correct dimensions
      const img = new Image();
      img.onload = () => {
        console.log(`Cropped image dimensions: ${img.width} × ${img.height}`);
        if (img.width !== CROP_WIDTH || img.height !== Math.round(CROP_HEIGHT)) {
          console.warn('Warning: Cropped image dimensions may not match target');
        }
      };
      img.src = URL.createObjectURL(croppedImageBlob);

      const fileName = `featured-image-${Date.now()}.jpg`;
      const croppedImageFile = new File([croppedImageBlob], fileName, {
        type: 'image/jpeg',
      });

      const previewUrl = URL.createObjectURL(croppedImageBlob);

      setFeaturedImage(croppedImageFile);
      setImagePreview(previewUrl);
      setSelectedFileName(`${fileName} (${CROP_WIDTH}×${Math.round(CROP_HEIGHT)})`);
      
      toast.dismiss('cropping');
      // toast.success('Image cropped successfully!');
      
    } catch (e) {
      setShowCropper(false);
      toast.dismiss('cropping');
      toast.error(`Error cropping image: ${e.message}`);
      console.error('Error cropping image:', e);
    }
  };

  const handleCancelCrop = () => {
    setShowCropper(false);
    setSelectedFileName('No file chosen');
    setCroppedAreaPixels(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const removeImage = useCallback(() => {
    setFeaturedImage(null);
    setImagePreview('');
    setSelectedFileName('No file chosen');
    setCroppedAreaPixels(null);

    if (fileInputRef.current) {
      fileInputRef.current.value = null;
    }
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!title.trim()) {
      toast.error("Please enter a title for your blog post.");
      return;
    }
    if (!description.trim()) {
      toast.error("Please enter a description for your blog post.");
      return;
    }
    if (!content || content === '<p><br></p>' || content === '') {
      toast.error("Please enter content for your blog post.");
      return;
    }

    setIsSubmitting(true);

    try {
      const submitData = new FormData();
      submitData.append('title', title);
      submitData.append('description', description);
      submitData.append('content', content);
      
      if (featuredImage) {
        submitData.append('featuredImage', featuredImage);
      }

      const response = await ApiClient().post('/admin/blog', submitData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      if (response.data.responseCode === 'success' || response.status === 201) {
        toast.success("Blog post published successfully!");
        setTimeout(() => {
          history.push("/blogs-list");
        }, 1500);
      } else {
        throw new Error(response.data.responseMessage || "Failed to create blog");
      }
    } catch (error) {
      console.error('Error creating blog:', error);
      const errorMessage = error.response?.data?.responseMessage?.[0] ||
        error.response?.data?.message ||
        "Failed to create blog. Please try again.";
      toast.error(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <React.Fragment>
      <ToastContainer />
      <div className="page-content">
        <MetaTags>
          <title>Add New Blog Post</title>
        </MetaTags>
        <Container fluid>
          <CardHeader className="d-flex mb-3 p-3 justify-content-between align-items-center">
            <h4>Add New Blog Post</h4>
            <Col xs={12} md={2} className="d-flex justify-content-end mt-md-0">
              <Button color="secondary" className="me-2" tag={Link} to="/blogs-list">
                Cancel
              </Button>
              <Button
                color="primary"
                onClick={handleSubmit}
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Publishing...' : 'Publish'}
              </Button>
            </Col>
          </CardHeader>

          <Row>
            <Col lg={12}>
              <Form>
                <FormGroup className="mb-3">
                  <Label for="title">Blog Title</Label>
                  <Input
                    type="text"
                    id="title"
                    placeholder="Enter blog title"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    disabled={isSubmitting}
                  />
                </FormGroup>

                <FormGroup className="mb-3">
                  <Label for="description">Blog Description</Label>
                  <Input
                    type="textarea"
                    id="description"
                    placeholder="Enter a short description of your blog post"
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    disabled={isSubmitting}
                    rows="3"
                  />
                </FormGroup>

                <FormGroup className="mb-3">
                  <Label>Featured Image</Label>
                  <div className="d-flex">
                    <Button
                      type="button"
                      color="primary"
                      onClick={() => fileInputRef.current.click()}
                      style={{
                        borderTopRightRadius: 0,
                        borderBottomRightRadius: 0
                      }}
                      disabled={isSubmitting}
                    >
                      Choose File
                    </Button>
                    <Input
                      type="text"
                      readOnly
                      value={selectedFileName}
                      style={{
                        flex: 1,
                        cursor: "pointer",
                        borderTopLeftRadius: 0,
                        borderBottomLeftRadius: 0
                      }}
                      onClick={() => fileInputRef.current.click()}
                      disabled={isSubmitting}
                    />
                    <input
                      type="file"
                      id="featuredImage"
                      accept="image/*"
                      onChange={handleImageUpload}
                      ref={fileInputRef}
                      style={{ display: "none" }}
                      disabled={isSubmitting}
                    />
                  </div>
                  
                  {imagePreview && (
                    <div className="mt-2 d-flex align-items-center">
                      <img
                        src={imagePreview}
                        alt="Preview"
                        style={{
                          width: '110px',
                          height: '38px',
                          objectFit: 'cover',
                          borderRadius: '4px',
                          border: '1px solid #ddd',
                          marginRight: '10px'
                        }}
                      />
                      <Button
                        color="danger"
                        size="sm"
                        className="btn"
                        style={{
                          width: "20px",
                          height: "20px",
                          borderRadius: "50%",
                          backgroundColor: "transparent",
                          color: "rgb(189, 9, 9)",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          cursor: "pointer",
                          marginBottom: "25px",
                          boxShadow: "none"
                        }}
                        onClick={removeImage}
                        disabled={isSubmitting}
                      >
                        x
                      </Button>
                    </div>
                  )}
                  
                  <small className="text-muted d-block mt-1">
                    Recommended size: {CROP_WIDTH} × {Math.round(CROP_HEIGHT)} pixels
                  </small>
                </FormGroup>

                <FormGroup className="mb-3">
                  <Label>Blog Content</Label>
                  <div key="quill-editor" style={{ minHeight: '400px', marginBottom: '50px' }}>
                    <ReactQuill
                      ref={quillRef}
                      value={content}
                      onChange={setContent}
                      placeholder="Write your blog content here..."
                      modules={modules}
                      formats={formats}
                      theme="snow"
                      style={{
                        height: '400px',
                        border: '1px solid #e9ecef',
                        borderRadius: '4px',
                      }}
                      disabled={isSubmitting}
                    />
                  </div>
                </FormGroup>
              </Form>
            </Col>
          </Row>
        </Container>
      </div>

      {/* Cropper Modal */}
      {showCropper && (
        <div className="crop-modal" style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 9999
        }}>
          <div className="crop-modal-content" style={{
            backgroundColor: 'white',
            borderRadius: '8px',
            padding: '20px',
            maxWidth: '90%',
            maxHeight: '90%',
            width: '800px',
            height: '650px'
          }}>
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h4>Crop your featured image</h4>
              <small className="text-muted">Target: {CROP_WIDTH} × {Math.round(CROP_HEIGHT)} px</small>
            </div>
            
            <div className="crop-container" style={{ 
              position: 'relative', 
              width: '100%', 
              height: '400px',
              backgroundColor: '#f8f9fa',
              border: '2px dashed #dee2e6',
              borderRadius: '8px',
              overflow: 'hidden',
              marginBottom: '15px'
            }}>
              <Cropper
                image={imageToCrop}
                crop={crop}
                zoom={zoom}
                aspect={CROP_ASPECT}
                onCropChange={setCrop}
                onCropComplete={onCropComplete}
                onZoomChange={setZoom}
                cropShape="rect"
                zoomWithWheel={true}
                minZoom={1}  // Increased min zoom to prevent pixelation
                maxZoom={3}  // Reduced max zoom to maintain quality
                wheelSpeed={0.1}
                restrictPosition={true}
                cropSize={{ width: 300, height: 104 }} // Show larger crop box preview
              />
            </div>
            
            <div className="crop-controls" style={{ 
              marginTop: '10px', 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'space-between' 
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                <label style={{ fontWeight: '500', whiteSpace: 'nowrap' }}>Zoom:</label>
                <input
                  type="range"
                  value={zoom}
                  min={1}
                  max={3}
                  step={0.05}
                  onChange={(e) => setZoom(parseFloat(e.target.value))}
                  style={{ width: '200px', margin: '0 10px' }}
                />
                <span style={{ minWidth: '50px', fontSize: '14px', fontWeight: '500' }}>
                  {Math.round(zoom * 100)}%
                </span>
                <small className="text-muted">
                  Crop area: {Math.round(croppedAreaPixels?.width || 0)}×{Math.round(croppedAreaPixels?.height || 0)}px
                </small>
              </div>
              
              <div className="crop-buttons" style={{ display: 'flex', gap: '10px' }}>
                <Button 
                  color="secondary" 
                  onClick={handleCancelCrop}
                  style={{ minWidth: '80px' }}
                >
                  Cancel
                </Button>
                <Button 
                  color="primary" 
                  onClick={handleCropComplete}
                  disabled={!croppedAreaPixels}
                  style={{ minWidth: '100px' }}
                >
                  {croppedAreaPixels ? 'Save Crop' : 'Adjust Crop'}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </React.Fragment>
  );
}

export default React.memo(AddBlogs);